/**
 *
 *
 * @author Juan Rico
 *
 * @module Informes
 *
 * @description Este módulo define el la pagina de Informes o `Reports`, que permite visualizar y gestionar informes.
 * El componente se encarga de la obtención y eliminación de informes, así como de la
 * visualización de informes individuales con sus respectivos detalles.
 *
 */

/**
 * Importaciones necesarias para el la pagina de informes.
 */
import React, { useContext, useEffect, useState, useRef } from "react"; // Importa React y hooks.
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"; // Componente para usar íconos de FontAwesome.
import { faClock, faStopwatch } from "@fortawesome/free-solid-svg-icons"; // Íconos específicos para el componente.
import {
  Ic_amiodarona,
  iconAdrenalina,
  iconReanimacion,
  iconDesfibrilacion,
  iconNotas,
  Ic_chrono_L,
  grayClock,
  Ic_rayo,
  ic_rayo_white,
  arrowDown,
} from "../../helpers/ImagesCache"; //Iconos especificos cargados en cache por el desarrolador
import { TimelineContext } from "../../helpers/Context"; // Contexto que proporciona información sobre la línea de tiempo.
import {
  formatMs,
  getTimeFromMiliseconds,
  getTimeAsMinutesSeconds,
  abreviatePcrText,
} from "../../helpers/helpers"; // funcion para formatear el tiempo
import { OptionsRitmoECGCritical } from "../../helpers/Constants"; // URL base para las solicitudes API.
import HistoryText from "../HistoryText/HistoryText";
import useTranslation from "../../hooks/useTranslation";
import { Tooltip } from "recharts";
import TimelineStartEnd from "../Timeline/TimelineStartEnd";
import TimelinePCR from "../Timeline/TimelinePCR";
import TimelineShoks from "../Timeline/TimelineShoks";
import TimelineRCP from "../Timeline/TimelineRCP";
import TimelineCompressionsAndVentilations from "../Timeline/TimelineCompressionsAndVentilations";
import TimelinePatientRhythm from "../Timeline/TimelinePatientRhythm";
import PercentagesBar from "../Timeline/PercentagesBar";
import AcronymsDisplay from "../AcronymsDisplay/AcronymsDisplay";

/**
 * Componente principal que maneja la visualización de informes.
 *
 * @function GraphicalReport
 * @returns {JSX.Element} Un elemento que representa la interfaz de informes.
 */

const GraphicalReport = ({
  historyReport,
  hideName,
  selectedReport,
  header,
  minimized,
}) => {
  /**
   * Contexto de la linea de tiempo.
   * @type {Object}
   */
  const ctxTimeline = useContext(TimelineContext);
  const { timeline } = useContext(TimelineContext);
  const { timeline: history } = useContext(TimelineContext);
  const T = useTranslation();
  const [currentReport, setCurrentReport] = useState(
    selectedReport || ctxTimeline
  );

  const [minimizedReport, setMinimizedReport] = useState(minimized);

  const data = currentReport?.timeline;

  const startTime = data.startCase || 0;
  const endTime = data.stopCase || 0;

  const parseEvents = (objectName) =>
    data.history
      .filter((event) => event.object === objectName)
      .map((e) => ({ ...e, time: Number(e.time) }))
      .filter((e) => !isNaN(e.time));

  // Filtramos eventos relevantes
  const pcrEvents = parseEvents("PCR");
  const rceEvents = parseEvents("RCE");
  const rcpEvents = parseEvents("RCP");
  const ecgEvents = parseEvents("ecgPaciente");
  const monitorEvents = parseEvents("monitor");

  const getTotalDuration = (intervals) => {
    return intervals.reduce((total, interval) => {
      if (interval.start != null && interval.stop != null) {
        return total + (interval.stop - interval.start);
      }
      return total;
    }, 0);
  };

  const totalPCRTime = getTotalDuration(currentReport?.timeline?.RCE);

  // Convert flat events to interval pairs
  const RCPIntervalPairs = [];
  for (
    let i = 0;
    i < rcpEvents.filter((item) => item.object === "RCP").length - 1;
    i += 2
  ) {
    const start = rcpEvents[i].time;
    const stop = rcpEvents[i + 1]?.time || endTime;

    // Only add if both are present
    if (start && stop) {
      RCPIntervalPairs.push({ start, stop });
    }
  }
  const totalRCPTime = getTotalDuration(RCPIntervalPairs);

  const totalCompressionsTime = currentReport?.timeline?.compressions?.reduce(
    (sum, item) => {
      if (item.start != null && item.stop != null) {
        return sum + (item.stop - item.start);
      }
      return sum;
    },
    0
  );

  // Construcción de los intervalos de "Tiempo Parada"
  let paradaData = [];

  pcrEvents.forEach((pcr, index) => {
    const rce = rceEvents.find((rce) => rce.time >= pcr.time);
    if (rce) {
      paradaData.push({
        name: `PCR ${index + 1}`,
        start: pcr.time,
        end: rce.time,
        distance: (pcr.time - startTime) / (endTime - startTime),
        percentage: ((rce.time - pcr.time) / (endTime - startTime)) * 100,
        fill: "#2aac9c99",
      });
    }
  });

  // Construcción de los intervalos de "ECG Paciente" y "Monitor"
  const buildTimelineData = (events) => {
    let timelineData = [];
    let accumulatedTime = startTime;

    events
      .sort((a, b) => a.time - b.time)
      .forEach((event, index, arr) => {
        let nextEnd = event.time;

        accumulatedTime = nextEnd;
        let nextActualEnd = arr[index + 1] ? arr[index + 1].time : endTime;
        let pcrActive = OptionsRitmoECGCritical.filter((i) => i == event.value);

        timelineData.push({
          name: event.value,
          start: event.time,
          distance: (event.time - startTime) / (endTime - startTime),
          end: nextActualEnd,
          percentage:
            ((nextActualEnd - event.time) / (endTime - startTime)) * 100,
          fill: pcrActive.length > 0 ? "#0009" : "#0002",
        });
      });
    return timelineData;
  };

  const ecgData = buildTimelineData(ecgEvents);
  const monitorData = buildTimelineData(monitorEvents);

/*** PERISHOCK ***/
// Desfibrilaciones (ordenadas por tiempo asc)
const defibEvents = data.history
  .filter((ev) => ev.object === "defibrillation")
  .sort((a, b) => a.time - b.time);

const DEAEvents = data.history
  .filter((ev) => ev.object === "DEA")
  .filter((ev) => ev.title.includes("DESCARGA"))
  .sort((a, b) => a.time - b.time);

const shockEvents = defibEvents?.length > 0 ? defibEvents : DEAEvents;

// Momentos en que se detienen compresiones
const compStopEvents = data.history
  .filter((ev) => ev.object === "compressions" && ev.title.includes("Detiene"))
  .sort((a, b) => a.time - b.time);

// Momentos en que se inician compresiones
const compStartEvents = data.history
  .filter((ev) => ev.object === "compressions" && ev.title.includes("Inicia"))
  .sort((a, b) => a.time - b.time);

// Momentos en que se detiene RCP (para cuando no se miden compresiones)
const rcpStopEvents = data.history
  .filter((ev) => ev.object === "RCP" && ev.title.includes("Detiene"))
  .sort((a, b) => a.time - b.time);

// Recorremos cada pausa (stop -> start) y verificamos si hubo una descarga dentro
const periShockList = [];

// Si hay eventos de compresiones, usar la lógica original
if (compStopEvents.length > 0) {
  compStopEvents.forEach((stopEvent) => {
    const nextStart = compStartEvents.find((start) => start.time > stopEvent.time);
    if (!nextStart) return;

    // Buscar desfibrilaciones que ocurrieron dentro de esta pausa
    const shocksInPause = shockEvents.filter(
      (shock) => shock.time >= stopEvent.time && shock.time <= nextStart.time
    );

    if (shocksInPause.length === 0) return;

    // Tomamos la primera desfibrilación de la pausa como referencia
    const defib = shocksInPause[0];

    // Calcular pausa perishock (tiempo total desde detener compresiones hasta reanudarlas)
    const periShockMs = nextStart.time - stopEvent.time;
    
    // Calcular pre-shock (tiempo desde detener compresiones hasta la desfibrilación)
    const preShockMs = defib.time - stopEvent.time;
    
    // Calcular post-shock (tiempo desde la desfibrilación hasta reanudar compresiones)
    const postShockMs = nextStart.time - defib.time;

    periShockList.push({
      defibTime: defib.time,
      preShock: preShockMs,
      postShock: postShockMs,
      periShock: periShockMs,
    });
  });
} else {
  // Si no hay eventos de compresiones, usar eventos RCP
  rcpStopEvents.forEach((stopEvent) => {
    // Buscar la siguiente desfibrilación después de detener RCP
    const nextShock = shockEvents.find((shock) => shock.time > stopEvent.time);
    if (!nextShock) return;

    // Calcular pausa perishock (tiempo total desde detener RCP hasta reanudar RCP)
    const nextRcpStart = data.history.find(
      (ev) => ev.object === "RCP" && ev.title.includes("Inicia") && ev.time > nextShock.time
    );
    
    const periShockMs = nextRcpStart 
      ? nextRcpStart.time - stopEvent.time 
      : nextShock.time - stopEvent.time + 5000; // Si no hay siguiente RCP, usar tiempo hasta shock + 5 segundos

    // Calcular pre-shock (tiempo desde detener RCP hasta la desfibrilación)
    const preShockMs = nextShock.time - stopEvent.time;
    
    // Calcular post-shock (tiempo desde la desfibrilación hasta reanudar RCP)
    const postShockMs = nextRcpStart 
      ? nextRcpStart.time - nextShock.time 
      : 5000; // Si no hay siguiente RCP, usar 5 segundos por defecto

    periShockList.push({
      defibTime: nextShock.time,
      preShock: preShockMs,
      postShock: postShockMs,
      periShock: periShockMs,
    });
  });
}

/*** END PERISHOCK ***/

  const onChangeMinimized = (value) => {
    setMinimizedReport(value);
  };
  /**
   * Obtiene el tiempo total de un objeto específico.
   *
   * @function getTotalTimeOf
   * @param {string} object - El nombre del objeto del que se desea obtener el tiempo.
   * @returns {string} El tiempo total en formato de minutos y segundos.
   */
  const getTotalTimeOf = (object) => {
    let timeTemp = 0;
    currentReport?.timeline?.[object]?.map((item) => {
      const time = item.stop - item.start;
      timeTemp += time;
    });
    const totalTime = new Date(timeTemp);
    let minutes = totalTime.getMinutes();
    let seconds = totalTime.getSeconds();
    seconds = seconds < 10 ? "0" + seconds : seconds;
    minutes = minutes < 10 ? "0" + minutes : minutes;
    return `${minutes}:${seconds}`;
  };

  /**
   * Obtiene el tiempo total de un objeto específico.
   *
   * @function getTotalTime
   * @param {string} object - El nombre del objeto del que se desea obtener el tiempo total.
   * @returns {number} El tiempo total en milisegundos.
   */
  const getTotalTime = (object) => {
    let timeTemp = 0;
    currentReport?.timeline?.[object]?.map((item) => {
      const time = item.stop - item.start;
      timeTemp += time;
    });
    return timeTemp;
  };

  /**
   * Suma el tiempo de un array de objetos.
   *
   * @function getSumaTime
   * @param {Array<number>} objects - Array de tiempos a sumar.
   * @returns {string} El tiempo total en formato de minutos y segundos.
   */
  const getSumaTime = (objects) => {
    let timeTemp = 0;
    objects.map((item) => {
      timeTemp += item;
    });
    const totalTime = new Date(timeTemp);
    let minutes = totalTime.getMinutes();
    let seconds = totalTime.getSeconds();
    seconds = seconds < 10 ? "0" + seconds : seconds;
    minutes = minutes < 10 ? "0" + minutes : minutes;
    return `${minutes}:${seconds}`;
  };

  /**
   * Obtiene el tiempo medio de un objeto específico.
   *
   * @function getMediaTimeOfHistory
   * @param {string} object - El nombre del objeto del que se desea obtener el tiempo medio.
   * @returns {string} El tiempo medio en formato de minutos y segundos.
   */
  const getMediaTimeOfHistory = (object, activeUnique = false) => {
    const list =
      currentReport?.timeline?.history?.filter(
        (item) => item.object == object
      ) || [];
    
    if (list.length === 0) {
      return `00:00`;
    }
    
    if (list.length === 1) {
      if (activeUnique) {
        const timeFromStart = list[0].time - currentReport?.timeline?.startCase;
        return getTimeAsMinutesSeconds(Math.max(0, timeFromStart));
      }
      return `00:00`;
    }
    
    // Calcular intervalos entre eventos consecutivos
    const sortedList = list.sort((a, b) => a.time - b.time);
    const intervals = [];
    
    for (let i = 1; i < sortedList.length; i++) {
      const interval = sortedList[i].time - sortedList[i - 1].time;
      if (interval > 0) {
        intervals.push(interval);
      }
    }
    
    if (intervals.length === 0) {
      return `00:00`;
    }
    
    const averageInterval = intervals.reduce((sum, interval) => sum + interval, 0) / intervals.length;
    return getTimeAsMinutesSeconds(Math.max(0, averageInterval));
  };

  useEffect(() => {
    console.log(
      "%cCurrent Report on Graphical Report",
      "color:orange",
      currentReport
    );
  }, [history, ctxTimeline, timeline, currentReport]);

  return (
    <>
      {!hideName && (
        <div className="w-full border-b-2 border-b-solid border-b-gay-500 pb-5 pt-5">
          <div className="text-xl font-[500] flex justify-between">
            <span>{currentReport?.name}</span>
          </div>
        </div>
      )}
      {historyReport && (
        <HistoryText
          selectedReport={currentReport}
          minimized={minimizedReport}
          onChangeMinimized={onChangeMinimized}
          monitorData={monitorData}
        />
      )}
      <div className="px-2 py-8 w-full mb-2 justify-between mt-2 relative bg-[#F9FAFB]">
        {/* START - END TIMELINE */}
        <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
          <div style={styles.leftTimelineContainer}>
            <div className="rounded flex justify-center items-center w-4 h-4 bg-black p-3 text-white">
              <FontAwesomeIcon icon={faStopwatch} />
            </div>
          </div>
          <div style={styles.timelineContainer}>
            <TimelineStartEnd
              endTime={getTimeFromMiliseconds(endTime - startTime)}
            />
          </div>
          <div style={styles.rightTimelineContainer}>
            <div className="w-full bg-white">
              <span className="flex justify-center w-full text-center text-lg font-bold">
                {T("Totales")}
              </span>
              <div className="flex w-full h-[50px] pb-[20px] items-center">
                <img src={Ic_chrono_L} className="w-[24px] h-full" />
                <span className="text-sm font-bold ">
                  {getTimeFromMiliseconds(endTime - startTime)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* PCR - RCE TIMELINE */}
        {currentReport?.timeline?.RCE?.length > 0 && (
          <div style={styles.categoryContainer}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center bg-white my-2 py-2 pl-3 text-lg font-bold">
                {T("PCR")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <TimelinePCR
                data={currentReport?.timeline?.RCE}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="h-full w-ful">
                <div className="flex w-full h-[50px] items-center">
                  <img src={Ic_chrono_L} className="w-[24px] h-[24px]" />
                  <span className="text-sm font-bold">
                    {getTimeFromMiliseconds(totalPCRTime)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* SHOKS TIMELINE */}
        {shockEvents?.length > 0 && (
          <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center  my-2 py-2 pl-3 text-lg font-bold">
                {T("Nº descargas")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <TimelineShoks
                data={shockEvents}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="w-full h-full bg-white"></div>
            </div>
          </div>
        )}
        {/* RCP TIMELINE */}
        {currentReport?.timeline?.history.filter(
          (item) => item.object === "RCP"
        )?.length > 0 && (
          <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center bg-white  my-2 py-2 pl-3 text-lg font-bold">
                {T("RCP")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <TimelineRCP
                data={currentReport?.timeline?.history}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="flex w-full h-[50px] items-center">
                <img src={Ic_chrono_L} className="w-[24px] h-[24px]" />
                <span className="text-sm font-bold">
                  {getTimeFromMiliseconds(totalRCPTime)}
                </span>
              </div>
            </div>
          </div>
        )}
        {/* COMPRESSIONS AND VENTILATIONS TIMELINE */}
        {(currentReport?.timeline?.compressions?.length > 0 ||
          currentReport?.timeline?.vents?.length > 0) && (
          <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center bg-white  my-2 py-2 pl-3 text-sm font-bold">
                {T("compressionsAndVentilations")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <TimelineCompressionsAndVentilations
                compressionsData={currentReport?.timeline?.compressions}
                ventilationsData={currentReport?.timeline?.vents}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="flex flex-col w-full h-full bg-white justify-center">
                <div className="flex flex-row items-center gap-2">
                  <div className="h-[8px] w-[16px] bg-[#228C80] rounded shrink-0"></div>
                  <span className="text-xs truncate">{T("compressions")}</span>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <div className="h-[8px] w-[16px] bg-[#F9839A] rounded shrink-0"></div>
                  <span className="text-xs truncate">{T("ventilations")}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ADRENALIN TIMELINE */}
        {currentReport?.timeline?.adrenalin?.length > 0 && (
          <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center  my-2 py-2 pl-3 text-lg font-bold">
                {T("Adrenalina")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <TimelineShoks
                data={currentReport?.timeline?.adrenalin}
                startTime={startTime}
                endTime={endTime}
                icon={iconAdrenalina}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="w-full h-full bg-white"></div>
            </div>
          </div>
        )}
        {/* AMIODARONE TIMELINE */}
        {currentReport?.timeline?.amiodarone?.length > 0 && (
          <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center  my-2 py-2 pl-3 text-lg font-bold">
                {T("Amiodarona")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <TimelineShoks
                data={currentReport?.timeline?.amiodarone}
                startTime={startTime}
                endTime={endTime}
                icon={Ic_amiodarona}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="w-full h-full bg-white"></div>
            </div>
          </div>
        )}
        {/* PATIENT RHYTHM TIMELINE */}
        {ecgData?.length > 0 && (
          <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center bg-white py-2 pl-3 text-lg font-bold">
                {T("patientRhythm")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <TimelinePatientRhythm
                data={ecgData}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="flex flex-col w-full h-full bg-white justify-center"></div>
            </div>
          </div>
        )}
        {/* PATIENT EVALUATION TIMELINE */}
        {monitorData?.length > 0 && (
          <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center bg-white py-2 pl-3 text-lg font-bold">
                {T("evaluateRhythm")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <TimelinePatientRhythm
                data={monitorData}
                startTime={startTime}
                endTime={endTime}
                abreviation={"evaluaRitmo"}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="flex flex-col w-full h-full bg-white justify-center"></div>
            </div>
          </div>
        )}
        {/* RCP FRACTION */}
        {(totalRCPTime > 0 || totalCompressionsTime > 0) && (
          <div style={{ ...styles.categoryContainer, ...styles.borderBottom }}>
            <div style={styles.leftTimelineContainer}>
              <span className="rounded w-full justify-center items-center bg-white py-2 pl-3 text-lg font-bold">
                {T("cprFraction")}
              </span>
            </div>
            <div style={styles.timelineContainer}>
              <PercentagesBar
                activeTime={totalRCPTime || totalCompressionsTime}
                totalTime={endTime - startTime}
              />
            </div>
            <div style={styles.rightTimelineContainer}>
              <div className="flex flex-col w-full h-full bg-white justify-center p-2">
                <div className="text-xs font-bold mb-2">Leyenda:</div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: "#F9839A" }}></div>
                  <span className="text-xs">&lt; 60%</span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: "#F4D947" }}></div>
                  <span className="text-xs">60-79%</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: "#2AAC9C" }}></div>
                  <span className="text-xs">80-100%</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* END GRAPHIC */}
      <div className="px-5 py-8 gap-4 w-full mb-10 flex flex-wrap justify-between mt-0 relative bg-[#EAF7F5]">
        {/* FARMACOS SCREEN */}
        {currentReport?.timeline?.type == "advanced" && (
          <div className="flex flex-col items-center gap-2 w-[49%] rounded-lg border bg-white border-[#DDE2E5] overflow-hidden">
            <div className="flex justify-between items-center w-full py-3 px-5 bg-[#F9FAFB]">
              <span className="text-[17px] font-bold">{T("Fármacos")}</span>
              <img className="h-6 w-6" src={Ic_amiodarona} alt="icon" />
            </div>

            <div className="w-full px-5 pb-4 flex flex-col gap-2">
              <div className="flex justify-between items-center w-full pt-2">
                <span className="text-[15px] font-semibold">
                  {T("Adrenalina")}:
                </span>
                <span className="text-[#2aac9c] font-bold text-[17px]">
                  {String(
                    currentReport?.timeline?.history?.filter(
                      (item) => item.object === "adrenalin"
                    )?.length
                  ).padStart(2, "0")}
                </span>
              </div>

              <div className="flex items-center w-full bg-[#F9FAFB] rounded-md px-3 py-2 shadow-sm border border-[#E3E5E7]">
                <img src={Ic_chrono_L} className="w-[30px] h-[30px] mr-2" />
                <span className="flex-1 text-[11px] text-gray-700">
                  {T("Tiempo medio de administración")}
                </span>
                <span className="font-semibold text-[16px] text-gray-900 ml-2 whitespace-nowrap">
                  {getMediaTimeOfHistory("adrenalin", true)}
                </span>
              </div>

              <div className="border-t border-gray-200 my-1" />

              <div className="flex justify-between items-center w-full pt-2">
                <span className="text-[15px] font-semibold">
                  {T("Amiodarona")}:
                </span>
                <span className="text-[#2aac9c] font-bold text-[17px]">
                  {String(
                    currentReport?.timeline?.history?.filter(
                      (item) => item.object === "amiodarone"
                    )?.length
                  ).padStart(2, "0")}
                </span>
              </div>

              <div className="flex items-center w-full bg-[#F9FAFB] rounded-md px-3 py-2 shadow-sm border border-[#E3E5E7]">
                <img src={Ic_chrono_L} className="w-[30px] h-[30px] mr-2" />
                <span className="flex-1 text-[11px] text-gray-700">
                  {T("Tiempo medio de administración")}
                </span>
                <span className="font-semibold text-[16px] text-gray-900 ml-2 whitespace-nowrap">
                  {getMediaTimeOfHistory("amiodarone", true)}
                </span>
              </div>

              <div className="border-t border-gray-200 my-1" />

              {currentReport?.timeline?.Farmacos?.map((farmaco, idx) => (
                <>
                  <div
                    key={idx}
                    className="flex justify-between items-center w-full pt-2"
                  >
                    <span className="text-[15px] font-semibold">
                      {T(farmaco)}:
                    </span>
                    <span className="text-[#2aac9c] font-bold text-[17px]">
                      {"01"}
                    </span>
                  </div>
                  <div className="border-t border-gray-200 my-1" />
                </>
              ))}
            </div>
          </div>
        )}
        {/* SHOKS SCREEN */}
        <div className="flex flex-col items-center w-[49%]  bg-white rounded border border-[#DDE2E5]">
          <div className="flex justify-between items-center w-full py-3 px-5 bg-[#F9FAFB] rounded-t">
            <span className="text-[17px] font-bold">{T("Descargas")}</span>
            <img className="h-6 w-6" src={iconDesfibrilacion} alt="icon" />
          </div>
          <div className="flex justify-between items-center w-full px-5 pt-3">
            <span className="text-sm font-bold">Nº {T("Descargas")}:</span>
            <span className="text-[#2aac9c] font-bold text-lg">
              {currentReport?.timeline?.defibrillation?.length >= 1 && (
                <>
                  {currentReport?.timeline?.defibrillation?.length < 10
                    ? "0" + currentReport?.timeline?.defibrillation?.length
                    : currentReport?.timeline?.defibrillation?.length}
                </>
              )}
              {currentReport?.timeline?.DEA?.length > 1 && (
                <>
                  {currentReport?.timeline?.DEA?.length < 10
                    ? "0" + currentReport?.timeline?.DEA?.length
                    : currentReport?.timeline?.DEA?.length}
                </>
              )}
              {currentReport?.timeline?.defibrillation?.length < 1 &&
                currentReport?.timeline?.DEA?.length <= 1 &&
                "00"}
            </span>
          </div>
          <div className="px-5 pr-3 overflow-y-auto overflow-x-hidden flex  flex-col gap-2 items-center w-full border-b border-b-solid border-b-2 pb-3">
            {shockEvents?.length > 0 && (
              <>
                {shockEvents?.map((item, index) => {
                  const defibrillationTime = item.stop || item.start || item.time;

                  const timeFromStart =
                    defibrillationTime - currentReport?.timeline?.startCase;

                  const previousDefibrilation =
                    currentReport?.timeline?.defibrillation[index - 1];
                  const previousDefibrilationTime =
                    previousDefibrilation?.stop || previousDefibrilation?.start;

                  const intervalTime = previousDefibrilation
                    ? defibrillationTime - previousDefibrilationTime
                    : null;

                  return (
                    <>
                      {intervalTime && (
                        <div className="flex justify-between items-center w-full gap-3">
                          <div className="flex justify-between w-full items-center gap-2">
                            <div className="flex gap-2 items-center">
                              <img
                                className="h-6 w-6 ml-2"
                                src={arrowDown}
                                alt={"icon"}
                              />
                            </div>
                          </div>
                          <span className="flex justify-end font-[500] text-right text-lg w-5/12 px-2 bg-[#F9FAFB] rounded">
                            {getTimeAsMinutesSeconds(intervalTime)}
                          </span>
                        </div>
                      )}
                      <div
                        className={`flex justify-between items-center px-2 w-full rounded gap-3 ${
                          index === 0 ? "bg-[#EDEDED]" : ""
                        }`}
                      >
                        <div className="flex justify-between w-full items-center gap-2">
                          <div className="flex gap-2 items-center">
                            <img
                              className="h-6"
                              src={index === 0 ? ic_rayo_white : Ic_rayo}
                              alt={"icon"}
                            />
                            {index === 0 && (
                              <span className="flex justify-end font-[500] text-right text-lg w-5/12 px-2 ">
                                {"1ª"}
                              </span>
                            )}
                          </div>
                        </div>
                        <span
                          className={`font-[500] text-rigth text-lg w-5/12 flex flex-end ${
                            index === 0 ? "" : "text-[#888586]"
                          }`}
                        >
                          {getTimeFromMiliseconds(timeFromStart)}
                        </span>
                      </div>
                    </>
                  );
                })}
              </>
            )}
          </div>
          <div className="px-5 py-3 flex justify-between items-center w-full border-b border-b-solid border-b-2 pb-3">
            <div className="flex items-center w-full bg-[#F9FAFB] rounded-md px-3 py-2 shadow-sm border border-[#E3E5E7]">
              <img src={Ic_chrono_L} className="w-[30px] h-[30px] mr-2" />
              <span className="flex-1 text-[11px] text-gray-700">
                {T("Media entre descargas")}
              </span>
              <span className="font-semibold text-[16px] text-gray-900 ml-2 whitespace-nowrap">
                {currentReport?.timeline?.defibrillation?.length > 0 && (
                  <>
                    {[
                      currentReport?.timeline?.defibrillation
                        ?.map((item, index) => {
                          if (!item?.hideOnReport) {
                            let totalTime = 0;
                            if (index > 0) {
                              const lastDefibrillationTime =
                                currentReport?.timeline?.history.filter(
                                  (historyItem) =>
                                    historyItem.object === "defibrillation"
                                );

                              totalTime =
                                lastDefibrillationTime[index]?.time -
                                lastDefibrillationTime[index - 1]?.time;
                            }
                            return totalTime;
                          }
                        })
                        .reduce((a, b) => a + b, 0),
                    ].map((time) => {
                      const itemsCount =
                        currentReport?.timeline?.defibrillation?.length - 1 ||
                        1;
                      const totalTime = new Date(time / itemsCount);
                      let minutes = totalTime.getMinutes();
                      let seconds = totalTime.getSeconds();
                      return `${minutes}′ ${
                        seconds < 10 ? "0" : ""
                      }${seconds}″`;
                    })}
                  </>
                )}
                {currentReport?.timeline?.DEA?.length > 0 &&
                  (() => {
                    const visibleDEAItems = currentReport.timeline.DEA.filter(
                      (item) => !item.hideOnReport
                    );

                    const durations = visibleDEAItems.map(
                      (item) => item.stop - item.start
                    );

                    const totalTime = durations.reduce((a, b) => a + b, 0);
                    const avgTime = totalTime / durations.length || 0;

                    const avgDate = new Date(avgTime);
                    const minutes = avgDate.getUTCMinutes(); // use UTC to avoid timezone issues
                    const seconds = avgDate.getUTCSeconds();

                    return (
                      <span>
                        {minutes}′ {seconds < 10 ? "0" : ""}
                        {seconds}″
                      </span>
                    );
                  })()}
              </span>
            </div>
          </div>
          <div className="flex justify-between items-center w-full px-5 pt-3">
            <span className="text-sm font-bold">{T("Pausa perishock")} - Tiempo medio:</span>
            <span className="text-[#2aac9c] font-bold text-lg">
              {periShockList.length == 0 ? (
                <span>{" " + T("No")}</span>
              ) : (
                <span>
                  {" " +
                    formatMs(
                      periShockList.reduce(
                        (acumulate, item) => acumulate + item.periShock,
                        0
                      ) / periShockList.length
                    )}
                </span>
              )}
            </span>
          </div>
          <div className="px-5 mt-2 max-h-[320px] pr-3 overflow-y-auto overflow-x-hidden flex  flex-col gap-2 items-center w-full pb-3">
            {periShockList.length > 0 && (
              <table className="w-full">
                <thead className="w-full">
                  <tr className="w-full">
                    <th className=" "></th>
                    {/* <th className='text-right '>Pre</th>*/}
                    {/* <th className='text-right '>Post</th>*/}
                    <th className=" "></th>
                  </tr>
                </thead>
                <tbody className="w-full">
                  {periShockList.map((item, index) => {
                    return (
                      <tr className="w-full">
                        <img className="h-6" src={grayClock} alt={"icon"} />
                        {/* <td className='font-[500] text-right  '> 
                                                {formatMs(item.preShock)}
                                            </td>  }
                                            {<td className='font-[500] text-right '> 
                                                {formatMs(item.postShock)}
                                            </td> */}
                        <td className="font-[500] text-right  ">
                          {formatMs(item.periShock)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
        {/* RCP SCREEN */}
        <div className="flex flex-col items-center w-[49%]  bg-white rounded border border-[#DDE2E5]">
          <div className="flex justify-between items-center w-full py-3 px-5 bg-[#F9FAFB] rounded-t">
            <span className="text-[17px] font-bold">RCP</span>
            <img className="h-6 w-6" src={iconReanimacion} alt="icon" />
          </div>
          <div className="flex justify-between items-center w-full px-5 pt-3">
            <span className="text-sm font-bold">{T("Tiempo RCP")}:</span>
            <span className="text-[#2aac9c] font-bold text-lg">
              {(() => {
                let totalTime = 0;
                if (
                  currentReport?.timeline?.compressions?.length > 0 &&
                  currentReport?.timeline?.vents?.length > 0
                ) {
                  totalTime =
                    getTotalTime("compressions") + getTotalTime("vents");
                } else if (currentReport?.timeline?.RCP?.length > 0) {
                  totalTime = getTotalTime("RCP");
                }
                const time = new Date(totalTime);
                const minutes = time.getMinutes();
                const seconds = time.getSeconds();
                return `${minutes}′ ${seconds < 10 ? "0" : ""}${seconds}″`;
              })()}
            </span>
          </div>
          <div className="flex flex-col gap-2 w-full px-5 pt-3">
            {[
              { label: T("Compresiones"), key: "compressions" },
              { label: T("Ventilaciones"), key: "vents" },
            ].map((item, i) => {
              const duration =
                currentReport?.timeline?.[item.key]?.reduce(
                  (total, el) => total + (el?.stop - el?.start),
                  0
                ) || 0;
              const time = new Date(duration);
              const minutes = time.getMinutes();
              const seconds = time.getSeconds();
              const formattedTime = `${minutes}′ ${
                seconds < 10 ? "0" : ""
              }${seconds}″`;

              return (
                <div className="flex items-center w-full bg-[#F9FAFB] rounded-md px-3 py-2 shadow-sm border border-[#E3E5E7]">
                  <img src={Ic_chrono_L} className="w-[30px] h-[30px] mr-2" />
                  <span className="flex-1 text-[11px] text-gray-700">
                    {item.label}
                  </span>
                  <span className="font-semibold text-[16px] text-gray-900 ml-2 whitespace-nowrap">
                    {duration ? formattedTime : "--:--"}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="w-full border-t border-[#E6E6E6] mt-3 px-5 py-4">
            <span className="text-sm font-bold">{T("Calidad RCP")}:</span>
            <div className="mt-2 flex flex-col gap-3">
              {currentReport?.timeline?.rcpQuality?.filter(
                (item) => item.selectedOption != null
              ).length === 0 && (
                <span className="text-sm text-gray-500">
                  {T("No se ha medido")}
                </span>
              )}
              {currentReport?.timeline?.rcpQuality?.map((item, index) => {
                if (item.selectedOption == null) return null;
                const {
                  title: resultTitle,
                  desc,
                  color,
                } = item.options[item.selectedOption];

                return (
                  <div
                    key={index}
                    className="flex justify-between items-center border-b border-[#E6E6E6] pb-2"
                  >
                    <span className="text-sm font-semibold max-w-[49%] truncate">
                      {T(item.title)}
                    </span>
                    <div
                      style={{ backgroundColor: color }}
                      className="rounded px-2 py-1 text-[12px] font-semibold text-black w-[90px] ml-2 flex flex-col "
                    >
                      <span className="leading-tight">{resultTitle}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* HABILITIES SCREEN */}
        <div className="flex flex-col items-center w-[49%]  bg-white rounded border border-[#DDE2E5]">
          <div className="flex justify-between items-center w-full py-3 px-5 bg-[#F9FAFB] rounded-t">
            <span className="text-[17px] font-bold leading-5">
              {T("Habilidades")}
              <br />
              {T("no técnicas")}
            </span>
            <img className="h-6 w-6" src={iconNotas} alt="icon" />
          </div>
          <div className="w-full px-5 py-4 flex flex-col gap-4">
            {currentReport?.timeline?.nonTechnical?.filter(
              (item) => item.selectedOption != null
            ).length === 0 && (
              <span className="text-sm text-gray-500">
                {T("No se ha medido")}
              </span>
            )}
            {currentReport?.timeline?.nonTechnical?.map((item, index) => {
              const selected = item.selectedOption;
              if (selected == null) return null;

              const {
                title: resultTitle,
                desc,
                color,
              } = item.options[selected];

              return (
                <div
                  key={index}
                  className="w-full border-b border-[#E6E6E6] pb-3"
                >
                  <div className="flex justify-between items-center w-full gap-2">
                    <span className="text-xs font-semibold w-[49%]">
                      {T(item.title)}
                    </span>
                    <div
                      style={{ backgroundColor: color }}
                      className="rounded px-2 py-1 text-[12px] font-semibold text-black w-[49%] flex flex-col"
                    >
                      <span className="leading-tight">{resultTitle}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      

    </>
  );
};
export default GraphicalReport;

//#region TABLA DE ESTILOS
const styles = {
  categoryContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  leftTimelineContainer: {
    display: "flex",
    width: "122px",
    alignItems: "center",
  },
  timelineContainer: {
    display: "flex",
    flex: 1,
    width: "100%",
    paddingLeft: "16px",
    paddingRight: "16px",
  },
  rightTimelineContainer: {
    width: "107px",
    paddingLeft: "6px",
    backgroundColor: "white",
  },
  borderBottom: {
    borderBottom: "1px solid #d1d5db",
  },
};
//#endregion
